/**
 * 派工（`host_jobs`）的顯示層共用邏輯：kind／狀態／階段中文、階段序列、耗時。
 *
 * 三份對照表刻意與後端 `app/services/jobs.py` 對齊，**改一邊要改兩邊**：
 * - `JOB_KIND_LABELS` ↔ `JOB_KINDS`（十二種硬編白名單）
 * - `OPERATOR_JOB_KINDS` ↔ `OPERATOR_KINDS`（維運人員能發的三種）
 * - `SUPERADMIN_JOB_KINDS` ↔ `SUPERADMIN_KINDS`（回收）
 *
 * ⚠️ 角色 gate 在 UI 只是「不給按」，真閘門是後端（fail-closed，operator 直打 API 照樣 403）。
 */
import { ROLE_RANK } from '@/stores/auth'
import type { HostJob } from '@/api/hosting'

// --- kind ------------------------------------------------------------------
export const JOB_KIND_LABELS: Record<string, string> = {
  collect_diagnostics: '收集診斷',
  run_backup: '執行備份',
  update_site: '升級站台',
  issue_cert: '簽發憑證',
  agent_upgrade: '升級 agent',
  provision_site: '開新站台',
  stop_site: '停站',
  resume_site: '復站',
  decommission_site: '回收站台',
  set_site_config: '調整站台設定',
  // 批151：站台機密的寫入／移出（批169 順手補：之前任務頁直接印英文 kind）。
  set_site_secret: '站台機密',
  // 批169：主機服務（全機共用的選配服務）的安裝／升級／移除——服務名在 params.service。
  host_service: '主機服務',
}
export function jobKindLabel(kind: string): string {
  return JOB_KIND_LABELS[kind] ?? kind
}

/** 維運人員（operator）可以發的 kind——其餘按鈕在 UI 就不該出現。 */
export const OPERATOR_JOB_KINDS = new Set(['update_site', 'collect_diagnostics', 'run_backup'])
/** 需要超級管理者的 kind（回收站台）。 */
export const SUPERADMIN_JOB_KINDS = new Set(['decommission_site'])

/**
 * 以角色 rank 判斷可否發某 kind。
 *
 * ★★ **一律具名比較，不要寫數字**（批110 修掉的那個坑）：原本是 `roleRank >= 3`，
 * 而批110 在中間插入 `manager` 之後 superadmin 變成 4 ——那一行會讓營運主管（3）在 UI
 * 看到「回收站台」，按下去卻被後端 `_gate_kind` 擋成 403（後端用的是
 * `ROLE_RANK["superadmin"]`，沒有這個問題）。
 */
export function canRunJobKind(kind: string, roleRank: number): boolean {
  if (SUPERADMIN_JOB_KINDS.has(kind)) return roleRank >= ROLE_RANK.superadmin
  if (roleRank >= ROLE_RANK.admin) return true
  return OPERATOR_JOB_KINDS.has(kind)
}

// --- 狀態 ------------------------------------------------------------------
export const JOB_STATE_LABELS: Record<string, string> = {
  queued: '排隊中',
  leased: '已派出',
  running: '執行中',
  succeeded: '已完成',
  failed: '失敗',
  expired: '逾時',
  canceled: '已取消',
}
export function jobStateLabel(state: string): string {
  return JOB_STATE_LABELS[state] ?? state
}
/** 對應 ui.css 的 `.status-pill` 變體。 */
export function jobStateClass(state: string): string {
  return (
    {
      queued: 'pending',
      leased: 'pending',
      running: 'pending',
      succeeded: 'active',
      failed: 'revoked',
      expired: 'revoked',
      canceled: 'superseded',
    }[state] ?? ''
  )
}

/** 終結態＝再也不會變（`services/jobs.py` 的 `TERMINAL_STATES`）。 */
export const TERMINAL_JOB_STATES: ReadonlySet<string> = new Set([
  'succeeded',
  'failed',
  'canceled',
  'expired',
])
export function isTerminalJobState(state: string): boolean {
  return TERMINAL_JOB_STATES.has(state)
}
/** 進行中（清單「進行中」分類與側欄徽章共用同一判準）。 */
export function isActiveJobState(state: string): boolean {
  return !TERMINAL_JOB_STATES.has(state)
}
/** 可重試的狀態（後端 `retry`：failed／expired／canceled）。 */
export function isRetryableJobState(state: string): boolean {
  return state === 'failed' || state === 'expired' || state === 'canceled'
}

/**
 * 不能直接重試的 kind（批150；對齊後端 `services/jobs.py::NO_RETRY_KINDS`，**改一邊要改兩邊**）。
 *
 * 重試＝同一組參數再建一筆，**不會**重跑專用端點的前置檢查與確認：回收要重新打字確認網域
 * （任務頁的「重試」是單鍵、沒有確認框），停站／復站要重新檢查「回收中／已回收」並寫停站原因，
 * 改站台設定要重新檢查「停用中的站」。這幾種請回主機頁重新執行；後端照樣 409 擋。
 */
export const NO_RETRY_JOB_KINDS: ReadonlySet<string> = new Set([
  'decommission_site',
  'stop_site',
  'resume_site',
  'set_site_config',
])

/**
 * 已被 agent 領走（leased／running）的**回收**不能取消（批150；後端 409 `decommission_in_progress`）：
 * agent 每次回報進度都會檢查取消旗標，回收做到一半停下來＝站台半毀，而且授權與 DNS 不會收尾。
 * 排隊中（queued）的回收可以取消，後端會把站台還原成回收前的狀態。
 */
export function isUncancelableDecommission(job: Pick<HostJob, 'kind' | 'state'>): boolean {
  return job.kind === 'decommission_site' && (job.state === 'leased' || job.state === 'running')
}

/** 進行中、且不是「已被領走的回收」——任務頁與清單的「取消」鈕用（角色另外判斷）。 */
export function isCancelableJob(job: Pick<HostJob, 'kind' | 'state'>): boolean {
  return isActiveJobState(job.state) && !isUncancelableDecommission(job)
}

/** 這筆任務能不能在任務頁按「重試」：狀態可重試、角色發得出這個 kind、且不是只能回主機頁重做的那幾種。 */
export function canRetryJob(job: Pick<HostJob, 'kind' | 'state'>, roleRank: number): boolean {
  return (
    isRetryableJobState(job.state) && canRunJobKind(job.kind, roleRank) && !NO_RETRY_JOB_KINDS.has(job.kind)
  )
}

// --- 診斷結果保存期（批150）--------------------------------------------------
/**
 * 診斷內容已逾保存期清除的時刻（ISO 字串）；沒清過／不是診斷任務＝`null`。
 *
 * 後端 `services/jobs.py::purge_diagnostics_sections` 把完成超過 `job_log_retention_days`
 * （預設 30 天）的 `collect_diagnostics` 結果拿掉 `sections`（站台日誌尾段，可能含 IP／email），
 * 寫上 `sections_purged_at`；其餘鍵（`truncated`／`bytes`／`site`／`tail_lines`）保留。
 * 任務頁看到它就改說「診斷內容已逾保存期清除」，而不是讓人對著一份沒有內容的 JSON 猜。
 */
export function diagnosticsPurgedAt(job: Pick<HostJob, 'kind' | 'result'>): string | null {
  if (job.kind !== 'collect_diagnostics') return null
  const at = job.result?.sections_purged_at
  return typeof at === 'string' && at ? at : null
}

// --- 階段 ------------------------------------------------------------------
/**
 * 階段中文。兩種來源混在同一個欄位（設計 §3.3）：
 * - **updater 語彙 8 個**（`update_site` 轉發 status.json）：queued／verifying／applying／
 *   healthy／rolling_back／rolled_back／failed／failed_needs_manual
 * - **開站 12 步**（agent 自己回報）：preflight…done
 * 未知值一律原樣顯示（agent 版本比中台新時不吞資訊）。
 */
export const JOB_PHASE_LABELS: Record<string, string> = {
  // updater 8 語彙
  queued: '排隊等候',
  verifying: '驗證更新包',
  applying: '套用更新',
  healthy: '健康檢查通過',
  rolling_back: '回滾中',
  rolled_back: '已回滾',
  failed: '失敗',
  failed_needs_manual: '失敗（需人工處理）',
  // 開站 12 步
  preflight: '環境檢查',
  identity: '指派 instance',
  license: '取得授權',
  fetch: '下載更新包',
  unpack: '解開檔案',
  install: '執行安裝',
  readback: '回讀實際設定',
  health: '健康檢查',
  dns: '建立 DNS 記錄',
  tls: '申請憑證',
  verify: '對外驗證',
  done: '完成',
  // --- agent 其餘作業回報的階段（批121）-------------------------------------
  //
  // ★ 這些是 `ipo-agent` 各 kind 自己 `ctx.progress("…")` 送上來的，除了開站那 12 步之外
  //   還有備份／停站／復站／回收／升級 agent／診斷等等。上面那份清單只涵蓋開站與 updater，
  //   於是〈任務〉頁的階段欄常常直接印 `purge`／`pending_restart`／`updater` 這種英文
  //   （主人 2026-09-21 回報）。這裡一次補齊 `grep progress(` 撈得到的全部。
  // ⚠️ 新增 agent kind 時記得回來補——`jobPhaseLabel` 查不到仍會原樣顯示（不吞資訊），
  //   所以漏了不會壞，只是又出現一個英文代碼。
  stage: '準備檔案',
  deps: '安裝相依套件',
  units: '設定系統服務',
  cron: '設定排程',
  write_env: '寫入設定檔',
  volumes: '準備資料目錄',
  activate: '啟用',
  starting: '啟動中',
  stopping: '停止中',
  down: '已停止',
  apply: '套用設定',
  backup: '備份',
  final_backup: '最後一次備份',
  packing: '打包',
  purge: '刪除資料',
  maintenance: '維護模式',
  smoke: '基本功能檢查',
  inspect: '檢查現況',
  collecting: '收集診斷資料',
  certbot: '申請／續發憑證',
  vhost: '設定網站入口',
  updater: '更新程式',
  pending_restart: '等待重啟',
  // 批169：主機服務（`host_service`）的階段（preflight／fetch／unpack／health 與開站共用上面的翻譯）。
  load: '載入 image',
  dirs: '建立目錄',
  env: '寫入服務設定',
  up: '啟動服務',
  cleanup: '回收舊版',
  images: '刪除 image',
  files: '刪除版本目錄',
}
export function jobPhaseLabel(phase: string | null): string {
  if (!phase) return '—'
  return JOB_PHASE_LABELS[phase] ?? phase
}

/**
 * 「異常」階段——不屬於正常流程、出現時要單獨標紅，不能硬塞進階段條。
 * （`failed` 同時是 updater 語彙也是異常標記，兩邊都吃得到。）
 */
export const ABNORMAL_PHASES: ReadonlySet<string> = new Set([
  'rolling_back',
  'rolled_back',
  'failed',
  'failed_needs_manual',
])

/**
 * 每種 kind 的正常階段序列（給階段條）。沒列的 kind＝agent 不回報細階段
 * （`[]`，畫面退回「派出 → 執行中 → 完成」的粗流程，不假造步驟）。
 */
const PHASE_SEQUENCES: Record<string, string[]> = {
  provision_site: [
    'preflight',
    'identity',
    'license',
    'fetch',
    'unpack',
    'install',
    'readback',
    'health',
    'dns',
    'tls',
    'verify',
    'done',
  ],
  update_site: ['queued', 'verifying', 'applying', 'healthy'],
}
export function jobPhaseSequence(kind: string): string[] {
  return PHASE_SEQUENCES[kind] ?? []
}

/**
 * 異常階段回推「當時走到哪一步」。回滾這三個語彙只可能發生在套用階段之後
 * （updater 先驗簽、再快照、才動容器）→ 對到 `applying`，階段條才不會整條變灰
 * （「全部沒跑」與「跑到套用才炸」是完全不同的故事）。
 * `failed` 沒有這種必然性，維持未知。
 */
const IMPLIED_PHASE: Record<string, string> = {
  rolling_back: 'applying',
  rolled_back: 'applying',
  failed_needs_manual: 'applying',
}

export type StepStatus = 'done' | 'current' | 'pending' | 'skipped'

export interface PhaseStep {
  phase: string
  label: string
  status: StepStatus
}

/**
 * 階段條的資料。規則：
 * - 目前階段之前＝done、之後＝pending；job 成功＝全部 done。
 * - job 失敗／逾時／取消而目前階段還在序列中＝該步標 current（紅由 view 依 state 上色），
 *   其後標 skipped（沒跑到，不是待跑）。
 * - `phase` 不在序列裡（異常語彙或 null）＝視為「還沒進入序列」，靠 `abnormal` 另外顯示。
 */
export function buildPhaseSteps(job: Pick<HostJob, 'kind' | 'state' | 'phase'>): PhaseStep[] {
  const seq = jobPhaseSequence(job.kind)
  if (!seq.length) return []
  const phase = job.phase ? (IMPLIED_PHASE[job.phase] ?? job.phase) : null
  const idx = phase ? seq.indexOf(phase) : -1
  const finished = job.state === 'succeeded'
  const aborted = job.state !== 'succeeded' && isTerminalJobState(job.state)
  return seq.map((phase, i) => {
    let status: StepStatus
    if (finished) status = 'done'
    else if (i < idx) status = 'done'
    else if (i === idx) status = 'current'
    else status = aborted ? 'skipped' : 'pending'
    return { phase, label: jobPhaseLabel(phase), status }
  })
}

/** 目前階段是不是異常語彙（回滾／需人工）——階段條旁另外標一顆紅籤。 */
export function jobAbnormalPhase(job: Pick<HostJob, 'phase'>): string | null {
  return job.phase && ABNORMAL_PHASES.has(job.phase) ? job.phase : null
}

// --- 耗時 ------------------------------------------------------------------
/** 秒 → `12 秒`／`3 分 05 秒`／`1 小時 12 分`。 */
export function fmtDuration(seconds: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '—'
  const s = Math.floor(seconds)
  if (s < 60) return `${s} 秒`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m} 分 ${String(s % 60).padStart(2, '0')} 秒`
  return `${Math.floor(m / 60)} 小時 ${String(m % 60).padStart(2, '0')} 分`
}

/**
 * 耗時：已終結＝started→finished（沒開跑過就用 created→finished，等待時間也算），
 * 仍在跑＝started→now。`now` 由呼叫端傳入（輪詢時一起更新，才不會每個 render 都跳動）。
 */
export function jobElapsed(job: HostJob, now: number = Date.now()): number | null {
  const startIso = job.started_at ?? job.created_at
  if (!startIso) return null
  const start = Date.parse(startIso)
  if (Number.isNaN(start)) return null
  const end = job.finished_at ? Date.parse(job.finished_at) : now
  if (Number.isNaN(end)) return null
  return (end - start) / 1000
}

/** log 等級 → CSS class（`.lg-warn`／`.lg-error`；info／debug 用預設色）。 */
export function logLevelClass(level: string): string {
  const l = level.toLowerCase()
  if (l === 'error' || l === 'fatal' || l === 'crit') return 'lg-error'
  if (l === 'warn' || l === 'warning') return 'lg-warn'
  if (l === 'debug' || l === 'trace') return 'lg-dim'
  return ''
}
